// ============================================================================
//  TIMELINE CONTENT  —  the only file you edit to change your timeline.
// ============================================================================
//
//  ➕  TO ADD AN ENTRY
//      1. Copy the TEMPLATE block below (the whole { ... }, including the
//         closing comma).
//      2. Paste it anywhere inside the `entries` list further down.
//      3. Fill in your details.
//      Order does NOT matter — entries auto-sort by date (newest first)
//      within their category. Just paste and go.
//
//  ─────────────────────────  TEMPLATE  ──────────────────────────────────────
//    {
//      id: "unique-slug",          // any unique text, no spaces (e.g. "acme-dev")
//      title: "Your Role / Project / Award",
//      org: "Company · School · Organization",
//      location: "Ottawa, ON",
//      start: "Jan 2027",          // "Mon YYYY"
//      end: "Present",             // "Mon YYYY"  |  "Present"  |  "Expected Mon YYYY"
//      current: true,              // true -> cyan "CURRENT" pill · false -> gold "COMPLETE"
//      category: "experience",     // experience | projects | education | leadership
//      summary: "One short line shown under the title.",
//      bullets: [
//        "First achievement sentence.",
//        "Second achievement sentence.",
//      ],
//      tags: ["Skill", "Tool"],    // small chips (optional)
//    },
//  ────────────────────────────────────────────────────────────────────────────
//
//  🏷️  The filter tabs come from `categories` below. To add a new tab, add a
//      line there, then use its `id` in your entries' `category` field.
// ============================================================================

// The filter tabs, in the order they appear. The FIRST one is selected by default.
export const categories = [
  { id: "experience", label: "Work Experience" },
  { id: "projects", label: "Projects" },
  { id: "education", label: "Education" },
  { id: "leadership", label: "Leadership" },
];

// ============================================================================
//  YOUR ENTRIES  —  add, edit, or remove freely. Order here doesn't matter.
// ============================================================================
const entries = [
  {
    id: "deloitte-qa",
    title: "Quality Assurance Engineer",
    org: "Deloitte",
    location: "Ottawa, ON",
    start: "May 2026",
    end: "Present",
    current: true,
    category: "experience",
    summary: "Manual & regression testing across B2B workflows and data pipelines.",
    bullets: [
      "Executed functional, integration, and end-to-end manual testing across complex B2B adaptor workflows, large-scale data pipelines, and internal reporting platforms; documented defects with clear reproduction steps in Jira and Zephyr Scale.",
      "Designed and maintained structured test plans and test cases, collaborating directly with Product Owners to validate acceptance criteria and ensure traceability from requirements to test coverage.",
      "Performed rigorous regression testing to ensure stable software releases and contributed QA perspectives in Agile/Scrum ceremonies including sprint planning, standups, and retrospectives.",
    ],
    tags: ["Jira", "Zephyr Scale", "Agile/Scrum", "Regression Testing"],
  },
  {
    id: "cityottawa-attendant",
    title: "Weight & Cardio Room Attendant",
    org: "City of Ottawa",
    location: "Ottawa, ON",
    start: "Aug 2023",
    end: "Present",
    current: true,
    category: "experience",
    summary: "Supervised fitness facilities and enforced safety policies.",
    bullets: [
      "Supervised up to 45 clients concurrently, ensuring proper and safe use of high-end fitness equipment.",
      "Enforced facility policies and controlled member access to maintain a secure and professional environment.",
    ],
    tags: ["Operations", "Safety", "Customer Care"],
  },
  {
    id: "cityottawa-csr",
    title: "Customer Service Representative",
    org: "City of Ottawa",
    location: "Ottawa, ON",
    start: "Jun 2023",
    end: "Present",
    current: true,
    category: "experience",
    summary: "Front-line client service in a fast-paced public facility.",
    bullets: [
      "Delivered consistent, high-quality client service in a fast-paced public-facing environment, managing inquiries and resolving concerns effectively.",
      "Strengthened community bonds by creating a safe and welcoming environment for facility users.",
    ],
    tags: ["Customer Service", "Communication"],
  },
  {
    id: "gmail-simplifier",
    title: "GmailSimplifier — AI Email Assistant",
    org: "Personal Project",
    location: "Ottawa, ON",
    start: "Jan 2026",
    end: "Mar 2026",
    current: false,
    category: "projects",
    summary: "Automated inbox prioritization using Google Gemini + Gmail APIs.",
    bullets: [
      "Built an automated inbox prioritization system by integrating Google Gemini and Gmail APIs for semantic analysis, achieving high-accuracy due-date extraction and content summarization.",
      "Implemented secure data synchronization by developing an OAuth 2.0 authentication flow to manage user tokens and programmatically update inbox thread labels.",
    ],
    tags: ["Python", "Google Gemini API", "Gmail API", "OAuth 2.0"],
  },
  {
    id: "battlesnake-winner",
    title: "BattleSnake Hackathon — Winner",
    org: "Hackathon",
    location: "Ottawa, ON",
    start: "Sep 2025",
    end: "Sep 2025",
    current: false,
    category: "projects",
    summary: "First place — competitive agent using A* search and flood fill.",
    bullets: [
      "Engineered a competitive Battlesnake using A* search and flood-fill algorithms to outperform 15 competing teams.",
      "Delivered full-cycle development in a high-autonomy team environment, maintaining strict code integration standards under time pressure.",
    ],
    tags: ["Python", "A* Search", "Flood Fill", "Winner"],
  },
  {
    id: "carleton-bcs",
    title: "B.CS (Honours) — AI & Machine Learning",
    org: "Carleton University",
    location: "Ottawa, ON",
    start: "Sep 2025",
    end: "Expected May 2029",
    current: true,
    category: "education",
    summary: "Honours Bachelor of Computer Science, concentration in AI & ML.",
    bullets: [
      "Pursuing an Honours Bachelor of Computer Science with a concentration in Artificial Intelligence and Machine Learning.",
      "Coursework and projects spanning algorithms, software testing, and applied machine learning.",
    ],
    tags: ["Computer Science", "AI/ML", "Algorithms"],
  },
  {
    id: "aca-coordinator",
    title: "Student Engagement Coordinator",
    org: "Asian Canadian Association — Carleton",
    location: "Ottawa, ON",
    start: "Sep 2025",
    end: "Apr 2026",
    current: false,
    category: "leadership",
    summary: "Grew student participation and connected members with the executive team.",
    bullets: [
      "Spearheaded initiatives to strengthen student participation through dynamic tabling sessions and membership drives.",
      "Formed direct links between the student body and the executive team, ensuring member voices are heard.",
      "Facilitated the technical and physical setup of promotional events to maximize visibility.",
    ],
    tags: ["Leadership", "Event Ops", "Community"],
  },
  {
    id: "och-volunteer",
    title: "Community Development Volunteer",
    org: "Ottawa Community Housing",
    location: "Ottawa, ON",
    start: "Aug 2024",
    end: "Aug 2024",
    current: false,
    category: "leadership",
    summary: "Designed and mounted recreational installations for the community.",
    bullets: [
      "Applied creative problem-solving to design and mount recreational installations, maximizing resource efficiency and minimizing costs.",
      "Collaborated with stakeholders to ensure project alignment with community needs and satisfaction benchmarks.",
    ],
    tags: ["Volunteering", "Problem Solving"],
  },
];

// ============================================================================
//  Auto-sort: newest first (by start date). You do NOT need to touch this.
// ============================================================================
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// Turn "May 2026" / "Present" / "Expected May 2029" into a sortable number.
function startValue(when) {
  const s = String(when || "").toLowerCase().replace("expected", "").trim();
  if (s.includes("present")) return Number.POSITIVE_INFINITY; // ongoing sorts to the top
  const match = s.match(/([a-z]{3})[a-z]*\s+(\d{4})/);
  if (!match) return 0;
  const month = Math.max(0, MONTHS.indexOf(match[1]));
  return Number(match[2]) * 12 + month;
}

export const timeline = [...entries].sort((a, b) => startValue(b.start) - startValue(a.start));
